import React, { useCallback, useEffect, useState } from "react";

import toast from "react-hot-toast";

import { CircleCheckIcon, FileIcon, SearchIcon } from "lucide-react";

import { formatDate } from "simplydo/core/dates";
import type { OpenAPI } from "simplydo/interfaces";
import type { UserMe } from "simplydo/interfaces/types/userTypes";
import { Button } from "simplydo/react/ui/button";
import { Dropdown } from "simplydo/react/ui/dropdown";
import { FormField, FormLabel } from "simplydo/react/ui/form";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H3 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Loader } from "simplydo/react/ui/loader";
import { Message } from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import api from "api";
import { countries } from "utils/countries";
import util from "utils/utils";

type VerificationReport =
  OpenAPI.GET<"/verifications/companies/reports">["response"]["reports"][number];
type VerificationCompany =
  OpenAPI.GET<"/verifications/companies/search">["response"]["companies"][number];

type CompanyVerificationReportGeneratorProps = {
  user: UserMe;
  trigger?: React.ReactElement<{ onClick?: () => void }>;
  forId: string;
  forType: "idea" | "org";
  content?: string;
  onComplete: (report: VerificationReport) => void;
  onError?: (error: { creditsafeEnabled: boolean; message: string }) => void;
};

function CompanyVerificationReportGenerator({
  user,
  trigger,
  forId,
  forType,
  content,
  onComplete,
  onError,
}: CompanyVerificationReportGeneratorProps) {
  const [companies, setCompanies] = useState<VerificationCompany[] | undefined>(
    undefined,
  );
  const [country, setCountry] = useState("GB");
  const [companyNumber, setCompanyNumber] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [reports, setReports] = useState<VerificationReport[]>([]);
  const [loadingReports, setLoadingReports] = useState<string | boolean>(false);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const userId = user?._id;

  useEffect(() => {
    if (open && userId) {
      setLoadingReports(true);
      api.verifications.getReports(
        (data) => {
          setLoadingReports(false);
          setReports(data.reports);
        },
        () => setLoadingReports(false),
      );
    }
  }, [userId, open]);

  const openModal = () => {
    setOpen(!open);
  };

  const searchForCompanies = useCallback(() => {
    setSearching(true);
    api.verifications.searchForCompanies(
      country,
      companyNumber,
      companyName,
      forType,
      forId,
      (data) => {
        setSearching(false);
        setCompanies(data.companies);
      },
      (err) => {
        setSearching(false);
        if (err.status === 503) {
          setOpen(false);
          onError?.({
            creditsafeEnabled: false,
            message:
              "We have detected an issue with the company lookup service. Please enter your information manually or contact support for assistance",
          });
        }
      },
    );
  }, [forType, forId, country, companyNumber, companyName, onError]);

  const chooseReport = useCallback(
    (report: VerificationReport) => {
      onComplete(report);
      setOpen(false);
    },
    [onComplete],
  );

  const generateReportForCompany = useCallback(
    (company: VerificationCompany) => {
      if (!company.regNo) return;
      setLoadingReports(company.regNo);
      api.verifications.createReport(
        { id: company._id, forType, forId },
        (data) => {
          onComplete(data.report);
          setLoadingReports(false);
          setOpen(false);
        },
        (err) => {
          toast.error(err.message);
          setLoadingReports(false);
        },
      );
    },
    [forType, forId, onComplete],
  );

  return (
    <>
      {trigger ? (
        React.cloneElement(trigger, { onClick: openModal })
      ) : (
        <Button size="small" fluid onClick={openModal} color="secondary">
          <Icon icon={FileIcon} />
          {content || "Generate a new report"}
        </Button>
      )}
      <Modal
        open={open}
        dimmer="inverted"
        size="small"
        style={{ zIndex: 100 }}
        closeOnDocumentClick={false}
      >
        <ModalHeader>Company lookup</ModalHeader>
        <ModalContent scrolling>
          <H3>Your companies</H3>
          {loadingReports && <Loader />}
          {reports?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Company name</TableHeaderCell>
                  <TableHeaderCell>Looked-up at</TableHeaderCell>
                  <TableHeaderCell />
                </TableRow>
              </TableHeader>
              <TableBody>
                {reports.map((report) => (
                  <TableRow key={report._id}>
                    <TableCell>
                      {report.companyName}
                      <br />
                      <small>{report.companyNumber}</small>
                    </TableCell>
                    <TableCell>{formatDate(report.createdAt)}</TableCell>
                    <TableCell collapsing>
                      <Button
                        size="small"
                        onClick={() => chooseReport(report)}
                        color="primary"
                      >
                        Use this company
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            !loadingReports && (
              <Message color="blue">
                You haven&apos;t looked-up any companies yet.
              </Message>
            )
          )}

          <H3>New company lookup</H3>
          <p>
            Search for your company by providing its country of registration{" "}
            <strong>and</strong> either its name or registration number.
          </p>

          <FormField>
            <FormLabel htmlFor="registrationCountry">
              Registration country (required)
            </FormLabel>
            <Dropdown
              variant="outline"
              search
              id="registrationCountry"
              value={country}
              onChange={(_e, s) => setCountry(`${s.value ?? "GB"}`)}
              placeholder="Select one"
              options={countries.map((c) => ({
                key: c.iso2,
                value: c.iso2,
                text: c.name,
              }))}
            />
          </FormField>

          <Grid columns={2} mobile={1}>
            <GridColumn>
              <FormField>
                <FormLabel htmlFor="registrationNumber">
                  Registration number
                </FormLabel>
                <Input
                  id="registrationNumber"
                  fluid
                  value={companyNumber}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCompanyNumber(e.target.value)
                  }
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === "Enter" || e.keyCode === 13) {
                      e.preventDefault();
                      searchForCompanies();
                    }
                  }}
                  placeholder="12345678"
                />
              </FormField>
            </GridColumn>
            <GridColumn>
              <FormField>
                <FormLabel htmlFor="companyName">Company name</FormLabel>
                <Input
                  id="companyName"
                  fluid
                  value={companyName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCompanyName(e.target.value)
                  }
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === "Enter" || e.keyCode === 13) {
                      e.preventDefault();
                      searchForCompanies();
                    }
                  }}
                  placeholder="My Company Ltd"
                />
              </FormField>
            </GridColumn>
          </Grid>
          <Button
            loading={searching}
            onClick={searchForCompanies}
            color="secondary"
          >
            <Icon icon={SearchIcon} />
            Search
          </Button>
          {companies?.length === 0 && (
            <Message size="small">
              We were unable to find any companies matching your search
              criteria.
            </Message>
          )}
          {companies && companies.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Company</TableHeaderCell>
                  <TableHeaderCell />
                </TableRow>
              </TableHeader>
              <TableBody>
                {companies.map((company) => (
                  <TableRow key={company.id}>
                    <TableCell>
                      {company.name}
                      <br />
                      <small>
                        <strong>{company.regNo}</strong>
                        <br />
                        {company.address?.simpleValue}
                      </small>
                    </TableCell>
                    <TableCell collapsing>
                      <Button
                        size="small"
                        loading={loadingReports === company.regNo}
                        disabled={
                          !!loadingReports && loadingReports !== company.regNo
                        }
                        onClick={() => generateReportForCompany(company)}
                        color="primary"
                      >
                        <Icon icon={CircleCheckIcon} />
                        Use this company
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </ModalContent>
        <ModalActions>
          <Button onClick={openModal} variant="outline">
            Close
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
}

export default CompanyVerificationReportGenerator;
